import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import {
  AdminEditor,
  AdminHeader,
  AdminImageInput,
  AdminInput,
  AdminSelect,
  AdminSpinner,
  ErrorToast,
  FormSeparator,
} from '../../adminComponents';

import {
  api,
  ADMIN_BLOGS,
  ADMIN_BLOGS_EDIT,
  AnimatedDiv,
  Button,
  Div,
  H5,
  handleError,
  getBlogById,
} from '@vgrubs/components';
import {
  BlogEntity,
  BlogHighlight,
  BlogType,
} from 'components/src/types/blog.types';
import { BlogCaseStudyFields } from './BlogCaseStudyFields';
import { blogPayload, hasHalfHighlight } from './blogPayload';
import { BLOG_TYPE_OPTIONS } from './data';

const typeOptions = (
  <>
    {BLOG_TYPE_OPTIONS.map(({ value, name }) => (
      <option value={value} key={value}>
        {name}
      </option>
    ))}
  </>
);

export const BlogsAdd = () => {
  const navigate = useNavigate();
  const match = useMatch(ADMIN_BLOGS_EDIT);
  const paramsId = match?.params.id;
  // A new post starts as the type of the list tab it was added from.
  const addedAs = (useLocation().state as { type?: BlogType } | null)?.type;
  const [blog, setBlog] = useState<BlogEntity | null>(
    paramsId === 'add' ? ({ type: addedAs ?? 'blog' } as BlogEntity) : null
  );
  const [loading, setLoading] = useState(false);

  async function getBlog() {
    try {
      setLoading(true);
      const { data } = await getBlogById(Number(paramsId));

      setBlog(data.data);
      setLoading(false);
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (paramsId !== 'add') {
      getBlog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (
    value: string,
    setState: Dispatch<SetStateAction<BlogEntity | null>>,
    name: keyof BlogEntity
  ) => {
    setState((prev) => {
      const copy = { ...prev } as BlogEntity;
      copy[name] = value as never;
      return copy;
    });
  };

  const setHighlights = (highlights: BlogHighlight[]) =>
    setBlog((prev) => ({ ...prev, highlights } as BlogEntity));

  const caseStudy = blog?.type === 'caseStudy';
  const noun = caseStudy ? 'case study' : 'blog';

  async function handleAddEdit() {
    const { imageUrl, id, createdAt, ...blogRest } = blog || {};

    let formData = new FormData();
    if (typeof imageUrl !== 'string') {
      formData.append('image', imageUrl!);
    }
    formData.append('blog', JSON.stringify(blogPayload(blogRest)));

    try {
      setLoading(true);

      if (paramsId === 'add') {
        await api.post(`/v1/blogs`, formData, {
          headers: { 'content-type': 'multipart/form-data' },
        });
      } else {
        await api.put(`/v1/blogs/${id}`, formData, {
          headers: { 'content-type': 'multipart/form-data' },
        });
      }

      setLoading(false);
      navigate(-1);
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!blog?.imageUrl) return ErrorToast('Please select Image');
    if (!blog?.title) return ErrorToast('Please enter Title');
    if (!blog?.content) return ErrorToast('Please enter Content');
    if (!blog?.shortText) return ErrorToast('Please enter Short text');
    if (!blog?.metaContent) return ErrorToast('Please enter Meta content');
    if (!blog?.metaTitle) return ErrorToast('Please enter Meta title');
    if (!blog?.urlPath) return ErrorToast('Please enter URL path');
    if (caseStudy && hasHalfHighlight(blog.highlights))
      return ErrorToast(
        'Please enter both the value and the label of every highlight'
      );

    handleAddEdit();
  }

  if (loading) return <AdminSpinner />;

  return (
    <>
      <AdminHeader />
      <AnimatedDiv>
        <H5 $m="20px 0">
          {paramsId !== 'add'
            ? `Edit ${noun} ${blog?.title}`
            : `Add new ${noun}`}
        </H5>
        <form onSubmit={handleSubmit}>
          <Div $flex $column $gap="32px">
            <AdminSelect
              label="Type"
              options={typeOptions}
              value={blog?.type ?? 'blog'}
              onChange={({ target }) =>
                handleChange(target.value, setBlog, 'type')
              }
            />

            {caseStudy && (
              <BlogCaseStudyFields
                client={blog?.client ?? null}
                highlights={blog?.highlights ?? null}
                onClient={(client) => handleChange(client, setBlog, 'client')}
                onHighlights={setHighlights}
              />
            )}

            <AdminImageInput
              image={blog?.imageUrl || ''}
              onChange={(img) =>
                handleChange(img as string, setBlog, 'imageUrl')
              }
              label="Logo"
            />

            <AdminInput
              label="Title"
              type="text"
              value={blog?.title}
              onChange={({ target }) =>
                handleChange(target.value, setBlog, 'title')
              }
              maxLength={70}
            />

            <AdminInput
              label="Meta Title"
              type="text"
              value={blog?.metaTitle || ''}
              onChange={({ target }) =>
                handleChange(target.value, setBlog, 'metaTitle')
              }
            />

            <AdminInput
              label="Meta Content"
              type="text"
              value={blog?.metaContent || ''}
              onChange={({ target }) =>
                handleChange(target.value, setBlog, 'metaContent')
              }
            />

            <AdminInput
              label="Meta Keywords"
              type="text"
              value={blog?.metaTags || ''}
              onChange={({ target }) =>
                handleChange(target.value, setBlog, 'metaTags')
              }
            />

            <AdminInput
              label="URL Path"
              type="text"
              value={blog?.urlPath}
              onChange={(e) => {
                handleChange(e.target.value.trim(), setBlog, 'urlPath');
              }}
            />

            <AdminInput
              label="Short text"
              type="text"
              value={blog?.shortText}
              onChange={({ target }) =>
                handleChange(target.value, setBlog, 'shortText')
              }
              maxLength={150}
            />

            <AdminEditor
              label="Content"
              onChange={(data) => handleChange(data, setBlog, 'content')}
              value={blog?.content}
            />
          </Div>
          <FormSeparator />
          <Div $flex $between>
            <Button type="button" onClick={() => navigate(ADMIN_BLOGS)} $gray>
              Cancel
            </Button>
            <Button type="submit">
              {caseStudy ? 'Save Case Study' : 'Save Blog'}
            </Button>
          </Div>
        </form>
      </AnimatedDiv>
    </>
  );
};
